import { CopropertyDto, UpdateCopropertyComplementsCommand } from 'api/coproperty';
import { CopropertyComplementsForm } from 'features/subscription-process/coproperty/CopropertyComplementsForm';
import { usePanel } from 'hooks/usePanel';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { copropertiesApi } from 'services/swagger-api';
import { useRefetchQuoteData } from '../../../hooks/useRefetchQuoteData';

interface EditCopropertyComplementsPanelProps {
  /** Copropriété du devis, telle que la contractualisation la renvoie. */
  coproperty: CopropertyDto;
}

/**
 * Sous-panneau « Contrat en cours et prochaine AG » de la contractualisation MRI : le
 * formulaire du parcours, enregistré ici même. Le panneau est rendu hors de l'arbre des
 * routes : il n'appelle pas `useCoproperty` (qui lit la copropriété du process du
 * risque) mais l'API avec l'id de la copropriété du devis, puis relit les données de
 * contractualisation et se referme sur le récapitulatif, resté ouvert dessous.
 */
export const EditCopropertyComplementsPanel: FC<EditCopropertyComplementsPanelProps> = ({ coproperty }) => {
  const { closePanel, setPanelActions } = usePanel();
  const refetchQuoteData = useRefetchQuoteData();
  const [isSaving, setIsSaving] = useState(false);

  const buildCommandRef = useRef<(() => UpdateCopropertyComplementsCommand | null) | null>(null);
  const registerBuildCommand = useCallback((build: () => UpdateCopropertyComplementsCommand | null) => {
    buildCommandRef.current = build;
  }, []);

  const handleSubmit = useCallback(async () => {
    const command = buildCommandRef.current?.();
    if (!command || !coproperty.id) return;

    setIsSaving(true);
    try {
      const response = await copropertiesApi.updateCopropertyComplements({
        copropertyId: coproperty.id,
        updateCopropertyComplementsCommand: command
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement des compléments a échoué.");
        return;
      }

      await refetchQuoteData();
      closePanel();
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue.');
    } finally {
      setIsSaving(false);
    }
  }, [coproperty.id, refetchQuoteData, closePanel]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'Annuler',
      submitLabel: isSaving ? 'Enregistrement…' : 'Enregistrer',
      submitDisabled: isSaving,
      onSubmit: handleSubmit
    });
  }, [setPanelActions, handleSubmit, isSaving]);

  return (
    <div className="flex flex-col gap-6">
      <CopropertyComplementsForm coproperty={coproperty} registerBuildCommand={registerBuildCommand} />
    </div>
  );
};
